"use client";

import { type FC, useState } from "react";
import { motion } from "framer-motion";
import type { IFilter } from "@/src/shared/features/search/types";
import { searchService } from "@/src/shared/features/search/services";
import SearchDestinationModal from "@/src/shared/features/search/components/SearchDestinationModal";
import { IOS_PUSH } from "@/src/shared/constants/animation";
import { useLockBodyScroll } from "@/src/shared/hooks/useLockBodyScroll";

interface IWhereModalMobileProps {
  filters: IFilter;
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>;
  onBack: () => void;
  /** Runs a recent search straight away. */
  onSearch?: (next: IFilter) => void;
  /** After a place is picked; the guided search moves on to the dates. Default: `onBack`. */
  onSelected?: () => void;
}

/**
 * The app's location search screen (`location_search.dart`), pushed in from the right as
 * the app's page: a pill search field with a back arrow, and the results under it.
 */
const WhereModal: FC<IWhereModalMobileProps> = ({
  filters,
  setFilters,
  onBack,
  onSearch,
  onSelected,
}) => {
  useLockBodyScroll();
  const [localFilters, setLocalFilters] = useState(filters);
  const typed = localFilters.destination?.name ?? "";

  const handleDestinationSelect = (next: IFilter) => {
    setFilters(next);
    (onSelected ?? onBack)();
  };

  const handleTyping = (name: string) =>
    setLocalFilters((prev) => ({
      ...prev,
      destination: {
        name,
        lat: null,
        lng: null,
        id: null,
        name_bn: null,
        order: null,
        tier_1: 1,
        tier_2: 5,
        within: 10,
      },
    }));

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={IOS_PUSH}
      className="fixed inset-0 z-50 flex flex-col bg-white pt-[env(safe-area-inset-top)] md:hidden"
    >
      <div className="px-4 pb-3.5 pt-3">
        <div className="flex h-[50px] items-center rounded-full border border-[#DDDDDD] bg-white focus-within:border-[#222222]">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="flex h-full shrink-0 items-center pl-4 pr-3"
          >
            <img
              src="/icons/search/listing_page_back.svg"
              alt=""
              className="h-6 w-6"
            />
          </button>
          <input
            type="search"
            enterKeyHint="search"
            autoComplete="off"
            placeholder="Search areas or hotels"
            aria-label="Search areas or hotels"
            value={searchService.isNearby(typed) ? "" : typed}
            onChange={(e) => handleTyping(e.target.value)}
            className="h-full min-w-0 flex-1 rounded-r-full border-0 bg-transparent pr-4 text-base font-medium text-ink caret-[#222222] outline-none placeholder:font-medium placeholder:text-ink-secondary focus:ring-0 [&::-webkit-search-cancel-button]:hidden"
            autoFocus
          />
        </div>
      </div>

      <div className="scrollbar-hide min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-6">
        <SearchDestinationModal
          isMobile
          filters={localFilters}
          setFilters={setLocalFilters}
          onDestinationSelect={handleDestinationSelect}
          onSearch={onSearch}
        />
      </div>
    </motion.div>
  );
};

export default WhereModal;
